import type Phaser from "phaser";
import { CharacterPlayback, type WireLook } from "odyssey-shared";
import type { CharacterFigure, Pose } from "../character.ts";
import { StripChoice } from "../character-strip.ts";
import { JumpShadow } from "../jump/shadow.ts";
import { FALLBACK_LOOK } from "./look-blocks.ts";
import type { LookCache } from "./look-cache.ts";

/**
 * No look waiting: what `next` holds while the character wears the look it was last told.
 */
const NO_LOOK = -1;

/**
 * A character drawn as one sprite over its look's block (ADR-094 decision 14): its frames are the block's cells,
 * tools included, in both stances, so it costs what a creature costs. It plays the strips the look layout cuts, chosen
 * as every character's are (`character-strip.ts`), and each frame asks the cache for its block: its look's once
 * composed, the fallback's until then and whenever a nearer look has taken its block. Nothing shows until the
 * fallback itself is drawn.
 *
 * `setPose` allocates nothing: the page's key and the frame's name are looked up, and the sprite's texture is set
 * only when its block or its cell changes. While a new look waits to be composed it asks for that look's block too,
 * so the look has its claim to one, and goes on showing the old. The origin comes with the frame, whose pivot is the cell's.
 *
 * While its `jump` strip plays, the sprite is drawn `lift` pixels above the feet and a ground shadow is drawn on the
 * feet line (`jump/shadow.ts`); the depth stays the feet's (ADR-116's third amendment).
 */
export class LookSprite implements CharacterFigure {
  /**
   * The strips the character plays beyond idle and walk.
   */
  public readonly playback = new CharacterPlayback();

  /**
   * Whether the character's weapon is drawn, which picks the drawn variants of its idle and walk strips.
   */
  public drawn = false;

  private readonly sprite: Phaser.GameObjects.Sprite;
  private readonly choice = new StripChoice();
  private readonly keys: string[];
  private id = FALLBACK_LOOK;
  private next = NO_LOOK;
  private block = 0;
  private cell = 0;
  private shown = false;
  private feetX = 0;
  private feetY = 0;
  private readonly shadow: JumpShadow;

  /**
   * @param scene The scene.
   * @param cache The look cache the character draws from.
   * @param depthOf Depth for a feet position in pixels, x then y, so the caller decides how characters sort with props.
   * @param look The look it wears, as it arrived; the fallback look when omitted, until `wear` says another.
   */
  public constructor(
    scene: Phaser.Scene,
    private readonly cache: LookCache,
    private readonly depthOf: (feetX: number, feetY: number) => number,
    look?: Readonly<WireLook>,
  ) {
    this.cell = this.cellNow();
    const key = cache.pageKey(0);
    this.keys = [key];
    this.sprite = scene.add.sprite(0, 0, key, cache.frame(0, this.cell)).setVisible(false);
    this.shadow = new JumpShadow(scene, this.sprite);
    if (look !== undefined) {
      this.wear(look);
    }
  }

  /**
   * The cell of the frame the choice names, or the cell shown when the layout lacks the strip or the frame.
   *
   * @returns The cell's place in the layout's `cells`.
   */
  private cellNow(): number {
    return this.cache.strips.cells[this.choice.anim]?.[this.choice.direction][this.choice.frame] ?? this.cell;
  }

  /**
   * The entry of the look the character wears, for the cache's `pin`.
   *
   * @returns The entry.
   */
  public get look(): number {
    return this.id;
  }

  /**
   * The entry of a look the character was told to wear and does not show yet, for the cache's `pin`.
   *
   * @returns The entry, or -1 when none waits.
   */
  public get waiting(): number {
    return this.next;
  }

  /**
   * Changes the look the character wears (ADR-094's amendment of 2026-10-06, B7). A character that shows a composed
   * look of its own keeps it, and its hold on it, until the new look's block is composed, so a change in the world
   * never flashes the fallback look; `setPose` makes the switch, and makes it at once if the look shown loses its
   * block while the new one waits (a nearer look took it, or the WebGL context was lost), since holding and asking
   * for a look that is being replaced would only have it composed again. A character that shows no look of its own, as an
   * entrant does, takes the new one at once and is drawn in the fallback until its block is composed. A look told
   * while another waits replaces the one waiting.
   *
   * @param look The look as it arrived.
   */
  public wear(look: Readonly<WireLook>): void {
    const cache = this.cache;
    const told = cache.retain(look);
    if (this.next !== NO_LOOK) {
      cache.release(this.next);
      this.next = NO_LOOK;
    }
    if (told === this.id) {
      cache.release(told);
      return;
    }
    if (this.id !== FALLBACK_LOOK && cache.composed(this.id)) {
      this.next = told;
      return;
    }
    cache.release(this.id);
    this.id = told;
  }

  /**
   * Whether the character is drawn as itself: the block it was last posed with is its own look's, composed. False
   * while it shows the fallback in its look's place, waiting for sheets or for a block, and in the frame its block is
   * composed after it was posed, until it is posed again.
   *
   * @returns True once its own look shows.
   */
  public get dressed(): boolean {
    return this.cache.composed(this.id) && this.block === this.cache.blockOf(this.id);
  }

  /**
   * The texture the character's shape is read from, for a copy drawn over it (ADR-094's amendment of 2026-10-07):
   * the page its sprite draws from, so the copy is the character as it is drawn, hair or none, clothes and tools,
   * and the fallback look whenever the character itself is drawn in it.
   *
   * @returns The page's texture key.
   */
  public get shapeKey(): string {
    return this.keys[0] as string;
  }

  /**
   * The frame of `shapeKey` the sprite shows: the cell it was last posed with, in the block it was last posed with.
   *
   * @returns The frame's name.
   */
  public get shapeFrame(): number {
    return this.cache.frame(this.block, this.cell);
  }

  /**
   * Places the character and picks its frame and direction: the pose's facing, except during a swing's strip, which
   * keeps the direction its first frame was drawn in. A hop's lift raises the sprite and shows the shadow while the
   * sprite shows; the depth is the feet's.
   *
   * @param pose Position in pixels (feet), facing and motion.
   * @param nowMs Scene time in ms.
   */
  public setPose(pose: Pose, nowMs: number): void {
    const cache = this.cache;
    this.choice.choose(cache.strips.anims, this.playback, pose.facing, pose.moving, pose.running === true, this.drawn, nowMs);
    const cell = this.cellNow();
    if (this.next !== NO_LOOK) {
      cache.block(this.next, pose.x, pose.y);
      if (cache.composed(this.next) || !cache.composed(this.id)) {
        cache.release(this.id);
        this.id = this.next;
        this.next = NO_LOOK;
      }
    }
    const block = cache.block(this.id, pose.x, pose.y);
    if (block !== this.block || cell !== this.cell) {
      const key = cache.pageKey(block);
      if (key === this.keys[0]) {
        this.sprite.setFrame(cache.frame(block, cell));
      } else {
        this.keys[0] = key;
        this.sprite.setTexture(key, cache.frame(block, cell));
      }
      this.block = block;
      this.cell = cell;
    }
    const lift = this.choice.lift;
    const depth = this.depthOf(pose.x, pose.y);
    this.feetX = pose.x;
    this.feetY = pose.y;
    this.sprite.setPosition(pose.x, pose.y - lift);
    this.sprite.setDepth(depth);
    const ready = cache.ready;
    if (ready !== this.shown) {
      this.shown = ready;
      this.sprite.setVisible(ready);
    }
    this.shadow.place(pose.x, pose.y, ready ? lift : 0, depth);
  }

  /**
   * The texture key the character draws from now: its block's page. One key, since a composed character is one
   * sprite.
   *
   * @returns The key.
   */
  public get stack(): string[] {
    return this.keys;
  }

  /**
   * The block the character draws from now: its look's, or 0 while it wears the fallback.
   *
   * @returns The block's number.
   */
  public get shownBlock(): number {
    return this.block;
  }

  /**
   * The frame a strip's strike lands on: a player's look names none, so its attack sounds as its strip starts.
   *
   * @returns Null.
   */
  public impactOf(): number | null {
    return null;
  }

  /**
   * The frame the character shows now, its place in its strip.
   *
   * @returns The frame index within its strip.
   */
  public get frame(): number {
    return this.choice.frame;
  }

  /**
   * The depth the character draws at now.
   *
   * @returns The sprite's depth.
   */
  public get depth(): number {
    return this.sprite.depth;
  }

  /**
   * The name of the strip the character shows now, as `look:<strip>:<direction>`, for tests and the HUD.
   *
   * @returns The name, made once per strip and direction; empty for a strip the layout lacks.
   */
  public get strip(): string {
    return this.cache.strips.anims[this.choice.anim]?.files[this.choice.direction] ?? "";
  }

  /**
   * Where the feet stand now, in world pixels, x.
   *
   * @returns The x it was last posed at.
   */
  public get x(): number {
    return this.feetX;
  }

  /**
   * Where the feet stand now, in world pixels, y: the true feet, below the sprite while a hop lifts it.
   *
   * @returns The y it was last posed at.
   */
  public get y(): number {
    return this.feetY;
  }

  /**
   * How far above its feet the character is drawn now.
   *
   * @returns The hop's lift in whole world pixels, 0 off the `jump` strip.
   */
  public get lift(): number {
    return this.choice.lift;
  }

  /**
   * The sprite's origin across the frame it shows, the cell's pivot.
   *
   * @returns The origin as a share of the frame's width.
   */
  public get originX(): number {
    return this.sprite.originX;
  }

  /**
   * The sprite's origin down the frame it shows, the cell's pivot.
   *
   * @returns The origin as a share of the frame's height.
   */
  public get originY(): number {
    return this.sprite.originY;
  }

  /**
   * Sets the opacity of the character's sprite.
   *
   * @param alpha The opacity, 0 to 1.
   */
  public setAlpha(alpha: number): void {
    this.sprite.setAlpha(alpha);
  }

  /**
   * Removes the character from the scene and lets go of its look, and of one that waited.
   */
  public destroy(): void {
    this.cache.release(this.id);
    this.id = FALLBACK_LOOK;
    if (this.next !== NO_LOOK) {
      this.cache.release(this.next);
      this.next = NO_LOOK;
    }
    this.shadow.destroy();
    this.sprite.destroy();
  }
}
